// ============================================================================
// SCENARIO TABLE PAGE
// ============================================================================
// Real poker table with predetermined cards from scenarios + coach overlay
// User plays actual hands with guidance and instant feedback
// FIXED: Mobile responsive layout - table full width, coach panel stacked
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  useToast,
  Grid,
  Divider,
  Progress,
  Collapse,
  Slide,
  Input,
  Flex,
  useBreakpointValue,
  IconButton,
} from '@chakra-ui/react';
import { 
  ArrowLeft, 
  Volume2, 
  VolumeX, 
  ChevronRight, 
  ChevronDown,
  ChevronUp,
  Target,
  Lightbulb,
  CheckCircle,
  XCircle,
  SkipForward,
  RefreshCw,
  BookOpen,
  Award,
  Brain,
  Info,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSkillLevel } from '../contexts/SkillLevelContext';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import PokerTableSVG from '../components/PokerTableSVG';
import gameEngine from '../services/gameEngine';
import handEvaluationService from '../services/handEvaluationService';
import {
  getRandomScenario,
  getScenarioById,
  getScenariosByConcept,
  getScenariosByDifficulty,
  formatScenarioForDisplay,
  getPreflopEquity,
  type Scenario,
  type ScenarioCard,
  type ScenarioConcept,
  type ScenarioDifficulty,
} from '../services/training/scenarioLibrary';
import {
  evaluateScenarioResponse,
} from '../services/training/pokerCoachService';
import { POKER_GLOSSARY, type GlossaryEntry } from '../services/training/trainingAnalysisService';
import { setGtoContext, clearGtoContext } from '../services/training/gtoContext';
import { handCodeFromCards } from '../data/gtoRanges';
import {
  makeBotDecision,
  getBotThinkingDelay,
  type BotPlayer,
} from '../services/training';
import { GameState, Card, PlayerAction } from '../types/GameState';
import { pssBustForBot } from '../utils/pssBots';

// ============================================================================
// SOUND SYSTEM - Uses your MP3 files in public/sounds/
// Maps simplified action names to actual filenames
// ============================================================================
const SOUND_FILES: { [key: string]: { file: string; volume: number } } = {
  // Turn & Cards
  'your-turn': { file: 'your-turn.mp3', volume: 0.5 },
  'card-deal': { file: 'card-deal.mp3', volume: 0.5 },
  'card-drop': { file: 'carddrop2-92718.mp3', volume: 0.5 },
  
  // Player Actions
  'fold': { file: 'folding-37422.mp3', volume: 0.5 },
  'check': { file: 'button-click.mp3', volume: 0.5 },
  'call': { file: 'poker_chips1-87592.mp3', volume: 0.5 },
  'chip-single': { file: 'poker_chips1-87592.mp3', volume: 0.5 },
  'bet': { file: 'poker_chips1-87592.mp3', volume: 0.5 },
  'raise': { file: 'poker_chips1-87592.mp3', volume: 0.5 },
  'all-in': { file: 'allinpushchips-96121.mp3', volume: 0.7 },
  
  // Results
  'win': { file: 'you-win-sequence-2-183949.mp3', volume: 0.6 },
  'lose': { file: 'marimba-lose-250960.mp3', volume: 0.4 },
  'error': { file: 'marimba-lose-250960.mp3', volume: 0.4 },
  
  // UI
  'button-click': { file: 'button-click.mp3', volume: 0.3 },
};

const playSound = (soundName: string, enabled: boolean) => {
  if (!enabled) return;
  try {
    const soundConfig = SOUND_FILES[soundName];
    if (!soundConfig) {
      console.warn(`Unknown sound: ${soundName}`);
      return;
    }
    const audio = new Audio(`/sounds/${soundConfig.file}`);
    audio.volume = soundConfig.volume;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore sound errors
  }
};

// ============================================================================
// CONSTANTS
// ============================================================================
const SMALL_BLIND = 1;
const BIG_BLIND = 2;
const STARTING_STACK = 200;

// Position mapping for 6-max table
const POSITION_TO_SEAT: Record<string, number> = {
  'UTG': 0,
  'UTG+1': 1,
  'MP': 1,
  'CO': 2,
  'BTN': 3,
  'SB': 4,
  'BB': 5,
};

// ============================================================================
// BEGINNER-FRIENDLY HELPERS
// ============================================================================

// Convert poker position to beginner-friendly explanation
const getPositionExplanation = (position: string): { name: string; emoji: string; explanation: string } => {
  const pos = position.toUpperCase();
  const explanations: Record<string, { name: string; emoji: string; explanation: string }> = {
    'UTG': { name: 'Under the Gun', emoji: '', explanation: 'First to act - tightest position' },

    'UTG+1': { name: 'Early Position', emoji: '', explanation: 'Second to act - still early' },

    'MP': { name: 'Middle Position', emoji: '', explanation: 'Middle of pack - moderate' },

    'CO': { name: 'Cutoff', emoji: '', explanation: 'One before dealer - good position' },

    'BTN': { name: 'Button (Dealer)', emoji: '', explanation: 'BEST position - act last!' },

    'SB': { name: 'Small Blind', emoji: '', explanation: 'Forced $1 bet - bad position' },

    'BB': { name: 'Big Blind', emoji: '', explanation: 'Forced $2 bet - act last preflop' },

  };
  return explanations[pos] || { name: position, emoji: '🃏', explanation: 'Table position' };
};

// Get hand strength description for beginners
const getHandStrengthSimple = (hand: [ScenarioCard, ScenarioCard]): { strength: string; emoji: string; tier: 'premium' | 'strong' | 'medium' | 'weak' | 'trash' } => {
  const [c1, c2] = hand;
  const r1 = c1.rank.toUpperCase();
  const r2 = c2.rank.toUpperCase();
  const suited = c1.suit === c2.suit;
  const paired = r1 === r2;
  
  // Premium pairs
  if (paired && ['A', 'K', 'Q', 'J'].includes(r1)) {
    return { strength: paired && r1 === 'A' ? 'BEST HAND IN POKER!' : 'Premium Pair', emoji: '', tier: 'premium' };

  }
  // Medium pairs
  if (paired) {
    return { strength: 'Pocket Pair', emoji: '', tier: parseInt(r1) >= 7 ? 'strong' : 'medium' };

  }
  // Big aces
  if (r1 === 'A' || r2 === 'A') {
    const kicker = r1 === 'A' ? r2 : r1;
    if (['K', 'Q', 'J', 'T'].includes(kicker)) {
      return { strength: suited ? 'Big Ace Suited' : 'Big Ace', emoji: suited ? '' : '', tier: 'strong' };

    }
    return { strength: 'Ace-x', emoji: '', tier: 'medium' };

  }
  // Suited connectors
  if (suited) {
    return { strength: 'Suited Cards', emoji: '', tier: 'medium' };

  }
  // Face cards
  if (['K', 'Q', 'J'].includes(r1) && ['K', 'Q', 'J'].includes(r2)) {
    return { strength: 'Face Cards', emoji: '', tier: 'medium' };

  }
  return { strength: 'Speculative Hand', emoji: '', tier: 'weak' };

};

// Get board texture explanation
const getBoardExplanation = (board: ScenarioCard[]): string => {
  if (board.length === 0) return 'No community cards yet (preflop)';
  if (board.length === 3) return 'The Flop - first 3 community cards';
  if (board.length === 4) return 'The Turn - 4th card revealed';
  if (board.length === 5) return 'The River - final card, last chance!';
  return '';
};

// Format card for display with suit symbol
const formatCardDisplay = (card: ScenarioCard): string => {
  const suitSymbols: Record<string, string> = {
    'hearts': '',

    'diamonds': '', 

    'clubs': '',

    'spades': '',

  };
  return `${card.rank}${suitSymbols[card.suit] || card.suit[0]}`;
};

// ============================================================================
// HELPERS
// ============================================================================

// Convert ScenarioCard to GameState Card
const convertCard = (sc: ScenarioCard): Card => ({
  rank: sc.rank,
  suit: sc.suit,
});

// Get seat index from position string
const getSeatFromPosition = (position: string): number => {
  const normalized = position.toUpperCase().replace('+', '+');
  return POSITION_TO_SEAT[normalized] ?? 3; // Default to BTN
};

// Generate bot players for other seats
const generateBots = (heroSeat: number, numPlayers: number = 6): BotPlayer[] => {
  const bots: BotPlayer[] = [];
  const botNames = ['Alex_Bot', 'Sarah_AI', 'Mike_Pro', 'Lisa_Shark', 'Dave_Fish'];
  const difficulties: Array<'easy' | 'medium' | 'hard'> = ['medium', 'medium', 'easy', 'hard', 'medium'];
  
  let nameIdx = 0;
  for (let seat = 0; seat < numPlayers; seat++) {
    if (seat !== heroSeat) {
      bots.push({
        uid: `bot_${seat}`,
        username: botNames[nameIdx % botNames.length],
        difficulty: difficulties[nameIdx % difficulties.length],
        style: 'balanced',
      });
      nameIdx++;
    }
  }
  return bots;
};

// ============================================================================
// COMPONENT
// ============================================================================

const ScenarioTablePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { currentUser, userProfile } = useAuth();
  const { recordScenarioCompleted } = useSkillLevel();
  
  // URL params
  const scenarioId = searchParams.get('scenario');
  const conceptFilter = searchParams.get('concept') as ScenarioConcept | null;
  const difficultyFilter = searchParams.get('difficulty') as ScenarioDifficulty | null;
  
  // Sound
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Scenario state
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [bots, setBots] = useState<BotPlayer[]>([]);
  const [heroSeat, setHeroSeat] = useState(0);
  
  // UI state - FIXED: Default to collapsed on mobile
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [showCoachPanel, setShowCoachPanel] = useState(!isMobile);
  const [showHint, setShowHint] = useState(false);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [glossarySearch, setGlossarySearch] = useState('');
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // Update coach panel when screen size changes
  useEffect(() => {
    if (isMobile !== undefined) {
      setShowCoachPanel(!isMobile);
    }
  }, [isMobile]);

  // Publish this scenario's seat and stack for the floating Charts button.
  // Stack sizes in the library are chips at 1/2 blinds unless the scenario
  // states an effective stack in big blinds.
  useEffect(() => {
    if (!scenario) return;
    const position = (scenario.setup.yourPosition || 'BTN').toUpperCase().replace('BUTTON', 'BTN');
    const stackBB = scenario.setup.effectiveStack ?? (scenario.setup.stackSizes.hero || 100) / 2;
    const facingRaise = !!scenario.setup.villainAction && /rais|3-?bet|jam|shove|all.?in/i.test(scenario.setup.villainAction);
    setGtoContext({
      position,
      stackBB,
      situation: stackBB <= 20 ? 'push' : facingRaise ? 'vs' : 'open',
      hand: handCodeFromCards(scenario.setup.yourHand),
      label: `${position} · ${Math.round(stackBB)} BB`,
    });
  }, [scenario]);
  useEffect(() => () => clearGtoContext(), []);
  
  // Feedback state
  const [feedback, setFeedback] = useState<{
    show: boolean;
    correct: boolean;
    evaluation: string;
    message: string;
    lesson: string;
  } | null>(null);
  
  // Session tracking
  const [scenariosCompleted, setScenariosCompleted] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  
  // Refs
  const botTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingBotRef = useRef(false);
  
  const currentUserId = currentUser?.uid || 'hero';
  const username = userProfile?.username || 'You';
  
  // ============================================================================
  // LOAD SCENARIO
  // ============================================================================
  
  const loadScenario = useCallback((newScenario?: Scenario) => {
    // Get scenario
    let targetScenario: Scenario;
    
    if (newScenario) {
      targetScenario = newScenario;
    } else if (scenarioId) {
      targetScenario = getScenarioById(scenarioId) || getRandomScenario();
    } else {
      targetScenario = getRandomScenario({
        concept: conceptFilter || undefined,
        difficulty: difficultyFilter || undefined,
      });
    }
    
    setScenario(targetScenario);
    setShowHint(false);
    setFeedback(null);
    setShowRaiseSlider(false);
    
    // Setup game state from scenario
    const seat = getSeatFromPosition(targetScenario.setup.yourPosition);
    setHeroSeat(seat);
    
    const generatedBots = generateBots(seat, 6);
    setBots(generatedBots);
    
    // Build players array
    const players: any[] = [];
    let botIdx = 0;
    
    //  Generate realistic varied stack sizes for bots

    // Each player should have different stack to look realistic
    const heroStack = targetScenario.setup.stackSizes.hero || 100;
    const generateRealisticStack = (): number => {
      // Real poker tables have varied stacks
      // Create a mix that looks natural
      const stackTypes = [
        () => heroStack * (1.8 + Math.random() * 0.4),   // Deep stack 180-220%
        () => heroStack * (1.3 + Math.random() * 0.3),   // Above average 130-160%
        () => heroStack * (0.95 + Math.random() * 0.2),  // Standard 95-115%
        () => heroStack * (0.7 + Math.random() * 0.2),   // Below average 70-90%
        () => heroStack * (0.4 + Math.random() * 0.2),   // Short stack 40-60%
        () => heroStack * (1.0 + Math.random() * 0.15),  // Average 100-115%
      ];
      
      // Pick random stack type
      const stackFn = stackTypes[Math.floor(Math.random() * stackTypes.length)];
      let stack = stackFn();
      
      // Round to look realistic (real chips are .00, .25, .50, .75)
      stack = Math.round(stack * 4) / 4;
      
      // Ensure reasonable bounds (min 20BB, max 400BB)
      const minStack = BIG_BLIND * 20;
      const maxStack = BIG_BLIND * 400;
      return Math.max(minStack, Math.min(maxStack, stack));
    };
    
    for (let i = 0; i < 6; i++) {
      if (i === seat) {
        // Hero
        players.push({
          id: currentUserId,
          chips: heroStack,
          seat: i,
          buyIn: heroStack,
          holeCards: targetScenario.setup.yourHand.map(convertCard),
          folded: false,
          eliminated: false,
          currentBet: 0,
          hasActed: false,
        });
      } else {
        // Bot with realistic varied stack
        const bot = generatedBots[botIdx];
        const botStack = generateRealisticStack();
        players.push({
          id: bot.uid,
          chips: botStack,
          seat: i,
          buyIn: botStack,
          holeCards: generateRandomHoleCards(),
          folded: false,
          eliminated: false,
          currentBet: 0,
          hasActed: false,
        });
        botIdx++;
      }
    }
    
    // Determine round from board
    const boardLength = targetScenario.setup.board.length;
    let round: 'preflop' | 'flop' | 'turn' | 'river' = 'preflop';
    if (boardLength >= 5) round = 'river';
    else if (boardLength === 4) round = 'turn';
    else if (boardLength === 3) round = 'flop';
    
    // Create game state
    const initialState: GameState = {
      id: `scenario_${Date.now()}`,
      tableId: 'scenario_table',
      players,
      communityCards: targetScenario.setup.board.map(convertCard),
      pot: targetScenario.setup.potSize,
      currentBet: targetScenario.setup.amountToCall || 0,
      dealerButton: (seat + 3) % 6, // Dealer is 3 seats before hero typically
      currentPlayerIndex: seat, // Hero acts
      round,
      status: 'playing',
      handNumber: scenariosCompleted + 1,
      smallBlind: SMALL_BLIND,
      bigBlind: BIG_BLIND,
    };
    
    //  FIX: Set up villain bets so chips show on table

    // Handle both scenarios with amountToCall AND scenarios with just villainAction
    const hasVillainAction = targetScenario.setup.villainAction && targetScenario.setup.villainAction.length > 0;
    const hasAmountToCall = targetScenario.setup.amountToCall && targetScenario.setup.amountToCall > 0;
    
    if (hasVillainAction || hasAmountToCall) {
      let villainBet = 0;
      let villainSeatIndex = -1;
      
      // Parse bet amount from villainAction string
      // Examples: "BTN raises to 5", "Villain bets $20", "BTN opens 2.5bb", "CO 3-bets to 15"
      if (hasVillainAction) {
        const action = targetScenario.setup.villainAction;
        // Match patterns like "raises to 5", "bets $20", "opens 2.5bb", "to 15"
        const betMatch = action.match(/(?:raises?|bets?|opens?|3-bets?)\s*(?:to\s*)?\$?(\d+(?:\.\d+)?)\s*(?:bb)?/i);
        if (betMatch) {
          let amount = parseFloat(betMatch[1]);
          // If it's in BB units (small number like 2.5), convert to dollars
          if (amount <= 10 && action.toLowerCase().includes('bb')) {
            amount = amount * BIG_BLIND; // Convert BB to dollars
          }
          // If the number is suspiciously small (like 2.5) and we're in a cash game context
          if (amount <= 5 && targetScenario.setup.stackSizes.hero >= 100) {
            amount = amount * BIG_BLIND; // Likely BB notation
          }
          villainBet = amount;
        }
        
        // Determine villain seat from action text
        const actionLower = action.toLowerCase();
        if (actionLower.includes('btn') || actionLower.includes('button')) {
          villainSeatIndex = players.findIndex(p => p.seat === POSITION_TO_SEAT['BTN']);
        } else if (actionLower.includes('co') || actionLower.includes('cutoff')) {
          villainSeatIndex = players.findIndex(p => p.seat === POSITION_TO_SEAT['CO']);
        } else if (actionLower.includes('utg')) {
          villainSeatIndex = players.findIndex(p => p.seat === POSITION_TO_SEAT['UTG']);
        } else if (actionLower.includes('mp') || actionLower.includes('middle')) {
          villainSeatIndex = players.findIndex(p => p.seat === POSITION_TO_SEAT['MP']);
        } else if (actionLower.includes('sb') && !actionLower.includes('hero')) {
          villainSeatIndex = players.findIndex(p => p.seat === POSITION_TO_SEAT['SB']);
        } else if (actionLower.includes('bb') && !actionLower.includes('hero')) {
          villainSeatIndex = players.findIndex(p => p.seat === POSITION_TO_SEAT['BB']);
        }
      }
      
      // Fallback: Calculate villain bet from amountToCall + hero's blind
      if (villainBet === 0 && hasAmountToCall) {
        const heroPos = targetScenario.setup.yourPosition?.toUpperCase();
        if (heroPos === 'BB') {
          villainBet = targetScenario.setup.amountToCall + BIG_BLIND;
        } else if (heroPos === 'SB') {
          villainBet = targetScenario.setup.amountToCall + SMALL_BLIND;
        } else {
          villainBet = targetScenario.setup.amountToCall;
        }
      }
      
      // Fallback: Use previous player if we couldn't determine villain seat
      if (villainSeatIndex === -1) {
        villainSeatIndex = (seat + 5) % 6;
      }
      
      // Apply villain bet
      if (villainBet > 0 && villainSeatIndex >= 0 && villainSeatIndex < initialState.players.length) {
        initialState.players[villainSeatIndex].currentBet = villainBet;
        initialState.currentBet = villainBet;
        console.log(`🎰 [SCENARIO] Villain at seat ${villainSeatIndex} bet $${villainBet}`);
      }
      
      // Set hero's posted blind
      const heroPos = targetScenario.setup.yourPosition?.toUpperCase();
      const heroIdx = initialState.players.findIndex(p => p.seat === seat);
      if (heroIdx >= 0) {
        if (heroPos === 'BB') {
          initialState.players[heroIdx].currentBet = BIG_BLIND;
        } else if (heroPos === 'SB') {
          initialState.players[heroIdx].currentBet = SMALL_BLIND;
        }
      }
    }
    
    setGameState(initialState);
    playSound('card-deal', soundEnabled);
    
  }, [scenarioId, conceptFilter, difficultyFilter, currentUserId, soundEnabled, scenariosCompleted]);
  
  // Generate random hole cards for bots
  const generateRandomHoleCards = (): Card[] => {
    const ranks = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
    const suits: Array<'hearts' | 'diamonds' | 'clubs' | 'spades'> = ['hearts', 'diamonds', 'clubs', 'spades'];
    return [
      { rank: ranks[Math.floor(Math.random() * ranks.length)], suit: suits[Math.floor(Math.random() * suits.length)] },
      { rank: ranks[Math.floor(Math.random() * ranks.length)], suit: suits[Math.floor(Math.random() * suits.length)] },
    ];
  };
  
  // Initial load
  useEffect(() => {
    loadScenario();
  }, []);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  const currentPlayer = gameState?.players.find(p => p.id === currentUserId);
  const isMyTurn = gameState?.players[gameState.currentPlayerIndex]?.id === currentUserId;
  
  const callAmount = useMemo(() => {
    if (!gameState || !currentPlayer) return 0;
    return Math.max(0, (gameState.currentBet || 0) - (currentPlayer.currentBet || 0));
  }, [gameState, currentPlayer]);
  
  const canCheck = callAmount === 0;
  const canRaise = currentPlayer && currentPlayer.chips > callAmount;
  const minRaise = gameState?.bigBlind || BIG_BLIND;
  const maxBet = gameState?.currentBet || 0;
  
  // Build players for PokerTableSVG
  const playersForSVG = useMemo(() => {
    if (!gameState) return [];
    
    return gameState.players.map((p, idx) => {
      const isHero = p.id === currentUserId;
      const bot = bots.find(b => b.uid === p.id);
      
      //  FIX: Use correct property names to match PokerTableSVG interface

      return {
        id: p.id,
        name: isHero ? username : (bot?.username || `Player ${idx + 1}`),
        // Practice bots wear the same PSS faces as live-table bots
        photoURL: !isHero && String(p.id).startsWith('bot_')
          ? pssBustForBot(p.id)
          : undefined,
        seat: p.seat,
        chips: p.chips,
        holeCards: isHero ? p.holeCards : undefined, // Only show hero's cards
        currentBet: p.currentBet || 0,
        folded: p.folded,
        eliminated: p.eliminated,
        isDealer: idx === gameState.dealerButton,
        isTurn: idx === gameState.currentPlayerIndex,
      };
    });
  }, [gameState, currentUserId, username, bots]);
  
  // ============================================================================
  // HANDLE PLAYER ACTION
  // ============================================================================
  
  const handleAction = useCallback(async (action: PlayerAction, amount?: number) => {
    if (!gameState || !scenario || isProcessingAction || feedback?.show) return;
    if (!isMyTurn) return;
    
    setIsProcessingAction(true);
    setShowRaiseSlider(false);
    
    // Play sound
    if (action === 'fold') playSound('fold', soundEnabled);
    else if (action === 'check') playSound('check', soundEnabled);
    else if (action === 'call') playSound('chip-single', soundEnabled);
    else if (action === 'raise' || action === 'all-in') playSound('raise', soundEnabled);
    
    // Map action for evaluation
    let evalAction: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in' = action as any;
    if (action === 'raise' && gameState.currentBet === 0) {
      evalAction = 'bet';
    }
    
    // Evaluate the action
    const evaluation = evaluateScenarioResponse(scenario, evalAction, amount);
    
    // Update stats
    setScenariosCompleted(prev => prev + 1);
    if (evaluation.correct) {
      setCorrectAnswers(prev => prev + 1);
      playSound('win', soundEnabled);
    }
    
    //  Record scenario for PSS Coins rewards (server-side validation)

    try {
      await recordScenarioCompleted(evaluation.correct, scenario.concept);
    } catch (err) {
      console.warn('Failed to record scenario completion:', err);
    }
    
    // Show feedback
    setFeedback({
      show: true,
      correct: evaluation.correct,
      evaluation: evaluation.evaluation,
      message: evaluation.feedback,
      lesson: evaluation.lesson,
    });
    
    setIsProcessingAction(false);
    
  }, [gameState, scenario, isProcessingAction, feedback, isMyTurn, soundEnabled]);
  
  // ============================================================================
  // NEXT SCENARIO
  // ============================================================================
  
  const handleNextScenario = useCallback(() => {
    setFeedback(null);
    loadScenario();
  }, [loadScenario]);
  
  // Skip scenario
  const handleSkipScenario = useCallback(() => {
    setScenariosCompleted(prev => prev + 1);
    loadScenario();
  }, [loadScenario]);
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (!scenario || !gameState) {
    return (
      <Box minH="100vh" bg={POKER_COLORS.darkBg} display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <RefreshCw size={40} color={POKER_COLORS.cyan} className="animate-spin" />
          <Text color="gray.400">Loading scenario...</Text>
        </VStack>
      </Box>
    );
  }
  
  const scenarioDisplay = formatScenarioForDisplay(scenario);
  
  return (
    <Box
      minH="100vh"
      bg={POKER_COLORS.darkBg}
      p={{ base: 1, md: 2 }}
      display="flex"
      flexDir="column"
      pb={{ base: '80px', md: 2 }} // Space for bottom nav on mobile
      // Phones: keep the header out from under the notch/status bar
      pt={{ base: 'max(8px, env(safe-area-inset-top))', md: 2 }}
    >
      {/* Header */}
      <HStack 
        justify="space-between" 
        p={2} 
        bg="blackAlpha.400" 
        borderRadius="md" 
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        <HStack>
          <Box
            as="button"
            onClick={() => navigate('/training')}
            p={2}
            borderRadius="md"
            _hover={{ bg: 'whiteAlpha.100' }}
          >
            <ArrowLeft size={20} color={POKER_COLORS.cyan} />
          </Box>
          <VStack align="start" spacing={0}>
            <HStack>
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                Scenario Practice
              </Text>
              <Badge colorScheme="purple" fontSize="xs">COACH</Badge>
            </HStack>
            <Text color="gray.500" fontSize="xs" display={{ base: 'none', sm: 'block' }}>
              {scenario.concept.replace(/-/g, ' ')} • {scenario.difficulty}
            </Text>
          </VStack>
        </HStack>
        
        <HStack spacing={2}>
          {/* Progress */}
          <HStack spacing={1} bg="blackAlpha.400" px={2} py={1} borderRadius="full">
            <Award size={14} color={POKER_COLORS.gold} />
            <Text color="white" fontSize="sm" fontWeight="bold">
              {correctAnswers}/{scenariosCompleted}
            </Text>
          </HStack>
          
          {/* Sound Toggle */}
          <Box
            as="button"
            onClick={() => setSoundEnabled(!soundEnabled)}
            p={2}
            borderRadius="md"
            bg={soundEnabled ? 'green.600' : 'gray.600'}
            color="white"
            _hover={{ opacity: 0.8 }}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </Box>
          
          {/* Skip */}
          <PokerButton
            variant="secondary"
            size="sm"
            onClick={handleSkipScenario}
            leftIcon={<SkipForward size={14} />}
            display={{ base: 'none', sm: 'flex' }}
          >
            Skip
          </PokerButton>
        </HStack>
      </HStack>
      
      {/* FIXED: Mobile-First Layout - Stack vertically on mobile */}
      <Flex 
        flex={1} 
        direction={{ base: 'column', md: 'row' }}
        gap={2}
        overflow="hidden"
      >
        {/* Mobile: Collapsible Coach Info Bar */}
        <Box display={{ base: 'block', md: 'none' }}>
          {/*  NEW: Beginner-Friendly "What's Happening" Panel */}

          <Box
            as="button"
            w="100%"
            onClick={() => setShowCoachPanel(!showCoachPanel)}
            bg={`linear-gradient(135deg, ${POKER_COLORS.darkTeal}ee 0%, ${POKER_COLORS.darkTeal}99 100%)`}
            p={3}
            borderRadius="lg"
            mb={2}
            border="2px solid"
            borderColor={POKER_COLORS.cyan}
          >
            <HStack justify="space-between">
              <HStack>
                <Target size={18} color={POKER_COLORS.gold} />
                <Text color="white" fontSize="sm" fontWeight="bold">
                   What's Happening?

                </Text>
              </HStack>
              <HStack>
                <Badge
                  bg={
                    scenario.difficulty === 'beginner' ? 'green.500' :
                    scenario.difficulty === 'intermediate' ? 'yellow.500' : 'red.500'
                  }
                  color="white"
                  fontSize="2xs"
                >
                  {scenario.difficulty.toUpperCase()}
                </Badge>
                {showCoachPanel ? <ChevronUp size={16} color="white" /> : <ChevronDown size={16} color="white" />}
              </HStack>
            </HStack>
          </Box>
          
          <Collapse in={showCoachPanel} animateOpacity>
            <Box
              bg={`linear-gradient(180deg, ${POKER_COLORS.darkTeal}dd 0%, ${POKER_COLORS.darkBg || '#0A1F2E'}ee 100%)`}
              borderRadius="lg"
              p={4}
              mb={2}
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              {/*  THE SITUATION - Main Header */}

              <Box 
                bg={`${POKER_COLORS.cyan}22`} 
                p={3} 
                borderRadius="lg" 
                mb={3}
                borderLeft="4px solid"
                borderColor={POKER_COLORS.cyan}
              >
                <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="sm" mb={2}>
                   THE SITUATION

                </Text>
                
                {/* Your Hand - Prominent */}
                {(() => {
                  const handInfo = getHandStrengthSimple(scenario.setup.yourHand);
                  return (
                    <VStack align="start" spacing={1} mb={2}>
                      <HStack>
                        <Text color="white" fontSize="md" fontWeight="bold">
                          Your Cards: {scenario.setup.yourHand.map(c => formatCardDisplay(c)).join(' ')}
                        </Text>
                        <Text fontSize="lg">{handInfo.emoji}</Text>
                      </HStack>
                      <Badge 
                        bg={
                          handInfo.tier === 'premium' ? 'green.500' :
                          handInfo.tier === 'strong' ? 'green.400' :
                          handInfo.tier === 'medium' ? 'yellow.500' :
                          handInfo.tier === 'weak' ? 'orange.500' : 'red.500'
                        }
                        color="white"
                        fontSize="xs"
                      >
                        {handInfo.strength}
                      </Badge>
                      
                      {/*  WIN PERCENTAGE - Professional Preflop Chart Data */}

                      {(() => {
                        // Use scenario equity if available, otherwise use preflop chart
                        const preflopData = getPreflopEquity(scenario.setup.yourHand[0], scenario.setup.yourHand[1]);
                        const isPostflop = scenario.setup.board && scenario.setup.board.length > 0;
                        const equity = scenario.equity?.hero || preflopData?.equity || 50;
                        const explanation = scenario.equity?.explanation || preflopData?.description || '';
                        const tier = preflopData?.tier || 'unknown';
                        const rank = preflopData?.rank || 0;
                        const handName = preflopData?.handName || '';
                        
                        // Tier display with proper speculative handling
                        const tierDisplay = {
                          premium: { label: 'PREMIUM', color: 'green' },

                          strong: { label: 'STRONG', color: 'teal' },

                          playable: { label: 'PLAYABLE', color: 'blue' },

                          marginal: { label: 'MARGINAL', color: 'yellow' },

                          speculative: { label: 'SPECULATIVE', color: 'purple' },

                          weak: { label: 'WEAK', color: 'red' },

                        }[tier] || { label: 'UNKNOWN', color: 'gray' };

                        
                        return (
                          <Box 
                            mt={2} 
                            p={2} 
                            bg="blackAlpha.400" 
                            borderRadius="md"
                            border="1px solid"
                            borderColor={equity >= 50 ? 'green.500' : 'orange.500'}
                            w="100%"
                          >
                            <HStack spacing={2}>
                              <Box 
                                w="50px" 
                                h="50px" 
                                borderRadius="full" 
                                bg={equity >= 60 ? 'green.500' : equity >= 40 ? 'yellow.500' : 'red.500'}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                border="3px solid"
                                borderColor={equity >= 60 ? 'green.300' : equity >= 40 ? 'yellow.300' : 'red.300'}
                              >
                                <Text color="white" fontWeight="bold" fontSize="md">
                                  {Math.round(equity)}%
                                </Text>
                              </Box>
                              <VStack align="start" spacing={0} flex={1}>
                                <HStack>
                                  <Text color="white" fontWeight="bold" fontSize="xs">
                                    {handName} 
                                  </Text>
                                  {rank <= 20 && (
                                    <Badge colorScheme="purple" fontSize="2xs">
                                      #{rank}
                                    </Badge>
                                  )}
                                </HStack>
                                <Badge 
                                  fontSize="2xs"
                                  colorScheme={tierDisplay.color}
                                >
                                  {tierDisplay.label}
                                </Badge>
                                <Text color="gray.400" fontSize="2xs" noOfLines={2}>
                                  {isPostflop ? explanation : preflopData?.description}
                                </Text>
                                {isPostflop && (
                                  <Badge colorScheme="cyan" fontSize="2xs" mt={1}>
                                    {scenario.setup.board.length === 3 ? 'FLOP' : 
                                     scenario.setup.board.length === 4 ? 'TURN' : 'RIVER'} EQUITY
                                  </Badge>
                                )}
                                {scenario.equity?.outs && (
                                  <Badge colorScheme="orange" fontSize="2xs" mt={1}>
                                    {scenario.equity.outs} outs
                                  </Badge>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        );
                      })()}
                    </VStack>
                  );
                })()}
                
                {/* Position - Explained */}
                {(() => {
                  const posInfo = getPositionExplanation(scenario.setup.yourPosition);
                  return (
                    <HStack spacing={2} mb={2}>
                      <Text fontSize="md">{posInfo.emoji}</Text>
                      <Box>
                        <Text color="white" fontSize="sm" fontWeight="semibold">
                          {posInfo.name}
                        </Text>
                        <Text color="gray.400" fontSize="xs">
                          {posInfo.explanation}
                        </Text>
                      </Box>
                    </HStack>
                  );
                })()}
                
                {/* Board */}
                {scenario.setup.board.length > 0 && (
                  <Box>
                    <Text color="gray.400" fontSize="xs" mb={1}>
                      {getBoardExplanation(scenario.setup.board)}
                    </Text>
                    <HStack spacing={1}>
                      {scenario.setup.board.map((card, i) => (
                        <Badge 
                          key={i} 
                          bg="white" 
                          color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}
                          fontSize="sm"
                          px={2}
                          py={1}
                        >
                          {formatCardDisplay(card)}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}
              </Box>
              
              {/*  THE ACTION - What happened */}

              <Box 
                bg={`${POKER_COLORS.gold}22`} 
                p={3} 
                borderRadius="lg" 
                mb={3}
                borderLeft="4px solid"
                borderColor={POKER_COLORS.gold}
              >
                <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize="sm" mb={2}>
                   WHAT HAPPENED

                </Text>
                <Text color="white" fontSize="sm" mb={2}>
                  {scenario.setup.streetAction}
                </Text>
                {scenario.setup.villainAction && (
                  <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
                     {scenario.setup.villainAction}

                  </Badge>
                )}
                <HStack mt={2} spacing={4}>
                  <Box>
                    <Text color="gray.500" fontSize="xs">Pot Size</Text>
                    <Text color="green.400" fontWeight="bold" fontSize="lg">${scenario.setup.potSize}</Text>
                  </Box>
                  {scenario.setup.amountToCall && scenario.setup.amountToCall > 0 && (
                    <Box>
                      <Text color="gray.500" fontSize="xs">To Call</Text>
                      <Text color="orange.400" fontWeight="bold" fontSize="lg">${scenario.setup.amountToCall}</Text>
                    </Box>
                  )}
                </HStack>
              </Box>
              
              {/*  YOUR DECISION */}

              <Box 
                bg="whiteAlpha.100" 
                p={3} 
                borderRadius="lg" 
                mb={3}
                textAlign="center"
              >
                <Text color="white" fontWeight="bold" fontSize="md" mb={1}>
                   What should you do?

                </Text>
                <Text color="gray.400" fontSize="xs">
                  This scenario tests: <Badge colorScheme="cyan" fontSize="xs">{scenario.concept.replace(/-/g, ' ').toUpperCase()}</Badge>
                </Text>
              </Box>
              
              {/* Hint Toggle */}
              <Box
                as="button"
                w="100%"
                p={2}
                bg="blackAlpha.300"
                borderRadius="md"
                onClick={(e) => { e.stopPropagation(); setShowHint(!showHint); }}
              >
                <HStack justify="space-between">
                  <HStack>
                    <Lightbulb size={14} color={POKER_COLORS.gold} />
                    <Text color={POKER_COLORS.gold} fontSize="xs"> Need a hint?</Text>

                  </HStack>
                  {showHint ? <ChevronUp size={14} color="gray" /> : <ChevronDown size={14} color="gray" />}
                </HStack>
              </Box>
              
              <Collapse in={showHint}>
                <Box mt={2} p={2} bg={`${POKER_COLORS.gold}11`} borderRadius="md" borderLeft="2px solid" borderColor={POKER_COLORS.gold}>
                  <Text color="gray.300" fontSize="xs">{scenario.keyLesson}</Text>
                </Box>
              </Collapse>

              {/* Mobile Glossary Toggle */}
              <Box
                as="button"
                w="100%"
                p={2}
                bg="blackAlpha.300"
                borderRadius="md"
                onClick={(e) => { e.stopPropagation(); setShowGlossary(!showGlossary); }}
              >
                <HStack justify="space-between">
                  <HStack>
                    <BookOpen size={14} color={POKER_COLORS.cyan} />
                    <Text color={POKER_COLORS.cyan} fontSize="xs"> Poker Terms</Text>

                  </HStack>
                  {showGlossary ? <ChevronUp size={14} color="gray" /> : <ChevronDown size={14} color="gray" />}
                </HStack>
              </Box>

              <Collapse in={showGlossary}>
                <Box mt={2} maxH="200px" overflowY="auto">
                  <Input
                    placeholder="Search terms..."
                    size="xs"
                    mb={2}
                    bg="blackAlpha.400"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    value={glossarySearch}
                    onChange={(e) => setGlossarySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {Object.values(POKER_GLOSSARY)
                    .filter((e: GlossaryEntry) => {
                      const s = glossarySearch.toLowerCase();
                      return !s || e.term.toLowerCase().includes(s) || e.definition.toLowerCase().includes(s);
                    })
                    .sort((a: GlossaryEntry, b: GlossaryEntry) => a.term.localeCompare(b.term))
                    .slice(0, 15)
                    .map((entry: GlossaryEntry, idx: number) => (
                      <Box key={idx} p={1.5} mb={1} bg="blackAlpha.300" borderRadius="sm">
                        <Text color="white" fontSize="2xs" fontWeight="bold">{entry.term}</Text>
                        <Text color="gray.400" fontSize="2xs">{entry.simpleDefinition || entry.definition}</Text>
                      </Box>
                    ))
                  }
                </Box>
              </Collapse>
            </Box>
          </Collapse>
        </Box>

        {/* Desktop: Left Coach Panel -  IMPROVED: Beginner-friendly */}

        <Collapse 
          in={showCoachPanel} 
          animateOpacity 
          style={{ minWidth: showCoachPanel ? '340px' : '0' }}
          unmountOnExit={false}
        >
          <VStack
            w="340px"
            h="100%"
            bg={`linear-gradient(180deg, ${POKER_COLORS.darkTeal}ee 0%, ${POKER_COLORS.darkBg || '#0A1F2E'}ee 100%)`}
            borderRadius="lg"
            p={4}
            spacing={4}
            align="stretch"
            display={{ base: 'none', md: 'flex' }}
            border="1px solid"
            borderColor="whiteAlpha.200"
            overflowY="auto"
            maxH="calc(100vh - 140px)"
          >
            {/* Scenario Title */}
            <HStack justify="space-between">
              <HStack>
                <Target size={20} color={POKER_COLORS.gold} />
                <Text color="white" fontWeight="bold" fontSize="md">
                   {scenario.title}

                </Text>
              </HStack>
              <Badge
                bg={
                  scenario.difficulty === 'beginner' ? 'green.500' :
                  scenario.difficulty === 'intermediate' ? 'yellow.500' : 'red.500'
                }
                color="white"
                fontSize="xs"
              >
                {scenario.difficulty.toUpperCase()}
              </Badge>
            </HStack>
            
            {/*  THE SITUATION */}

            <Box 
              bg={`${POKER_COLORS.cyan}22`} 
              p={4} 
              borderRadius="lg"
              borderLeft="4px solid"
              borderColor={POKER_COLORS.cyan}
            >
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="sm" mb={3}>
                 THE SITUATION

              </Text>
              
              {/* Your Hand - Prominent */}
              {(() => {
                const handInfo = getHandStrengthSimple(scenario.setup.yourHand);
                return (
                  <VStack align="start" spacing={2} mb={3}>
                    <HStack>
                      <Text color="white" fontSize="lg" fontWeight="bold">
                        Your Cards:
                      </Text>
                      <HStack spacing={1}>
                        {scenario.setup.yourHand.map((card, i) => (
                          <Badge 
                            key={i}
                            bg="white" 
                            color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}
                            fontSize="md"
                            px={2}
                            py={1}
                          >
                            {formatCardDisplay(card)}
                          </Badge>
                        ))}
                      </HStack>
                      <Text fontSize="xl">{handInfo.emoji}</Text>
                    </HStack>
                    <Badge 
                      bg={
                        handInfo.tier === 'premium' ? 'green.500' :
                        handInfo.tier === 'strong' ? 'green.400' :
                        handInfo.tier === 'medium' ? 'yellow.500' :
                        handInfo.tier === 'weak' ? 'orange.500' : 'red.500'
                      }
                      color="white"
                      fontSize="sm"
                      px={3}
                    >
                      {handInfo.strength}
                    </Badge>
                    
                    {/*  WIN PERCENTAGE - Professional Preflop Chart Data */}

                    {(() => {
                      const preflopData = getPreflopEquity(scenario.setup.yourHand[0], scenario.setup.yourHand[1]);
                      const isPostflop = scenario.setup.board && scenario.setup.board.length > 0;
                      const equity = scenario.equity?.hero || preflopData?.equity || 50;
                      const explanation = scenario.equity?.explanation || preflopData?.description || '';
                      const tier = preflopData?.tier || 'unknown';
                      const rank = preflopData?.rank || 0;
                      const handName = preflopData?.handName || '';
                      
                      // Tier display with proper speculative handling
                      const tierDisplay = {
                        premium: { label: 'PREMIUM', color: 'green' },

                        strong: { label: 'STRONG', color: 'teal' },

                        playable: { label: 'PLAYABLE', color: 'blue' },

                        marginal: { label: 'MARGINAL', color: 'yellow' },

                        speculative: { label: 'SPECULATIVE', color: 'purple' },

                        weak: { label: 'WEAK', color: 'red' },

                      }[tier] || { label: 'UNKNOWN', color: 'gray' };

                      
                      return (
                        <Box 
                          mt={3} 
                          p={3} 
                          bg="blackAlpha.500" 
                          borderRadius="lg"
                          border="2px solid"
                          borderColor={equity >= 50 ? 'green.400' : 'orange.400'}
                        >
                          <HStack spacing={4}>
                            <Box 
                              w="70px" 
                              h="70px" 
                              borderRadius="full" 
                              bg={`linear-gradient(135deg, ${equity >= 60 ? '#22c55e' : equity >= 40 ? '#eab308' : '#ef4444'} 0%, ${equity >= 60 ? '#15803d' : equity >= 40 ? '#ca8a04' : '#dc2626'} 100%)`}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              boxShadow="0 4px 15px rgba(0,0,0,0.3)"
                              position="relative"
                            >
                              <VStack spacing={0} flex={{ base: 1, md: 'initial' }}>
                                <Text color="white" fontWeight="bold" fontSize="xl" lineHeight={1}>
                                  {Math.round(equity)}%
                                </Text>
                                <Text color="whiteAlpha.800" fontSize="2xs">
                                  {isPostflop ? 'EQUITY' : 'vs Random'}
                                </Text>
                              </VStack>
                              {/* Rank badge */}
                              {rank <= 25 && (
                                <Box
                                  position="absolute"
                                  top="-5px"
                                  right="-5px"
                                  bg="purple.500"
                                  borderRadius="full"
                                  w="24px"
                                  h="24px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  border="2px solid white"
                                  boxShadow="md"
                                >
                                  <Text color="white" fontWeight="bold" fontSize="xs">
                                    #{rank}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                            <VStack align="start" spacing={1} flex={1}>
                              <HStack>
                                <Text color="white" fontWeight="bold" fontSize="lg">
                                  {handName}
                                </Text>
                                <Badge 
                                  fontSize="sm"
                                  colorScheme={tierDisplay.color}
                                  px={2}
                                >
                                  {tierDisplay.label}
                                </Badge>
                              </HStack>
                              <Text color="gray.300" fontSize="sm">
                                {isPostflop ? explanation : preflopData?.description}
                              </Text>
                              <HStack spacing={2} mt={1}>
                                {isPostflop && (
                                  <Badge colorScheme="cyan" fontSize="xs">
                                    {scenario.setup.board.length === 3 ? 'FLOP' : 
                                     scenario.setup.board.length === 4 ? 'TURN' : 'RIVER'}
                                  </Badge>
                                )}
                                {scenario.equity?.outs && (
                                  <Badge colorScheme="orange" fontSize="xs">
                                    {scenario.equity.outs} outs
                                  </Badge>
                                )}
                                {scenario.equity?.oddsToHit && (
                                  <Badge colorScheme="purple" fontSize="xs">
                                    {scenario.equity.oddsToHit}
                                  </Badge>
                                )}
                                {scenario.equity?.potOdds && (
                                  <Badge colorScheme="yellow" fontSize="xs">
                                    Pot: {scenario.equity.potOdds}
                                  </Badge>
                                )}
                              </HStack>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })()}
                  </VStack>
                );
              })()}
              
              {/* Position - Explained */}
              {(() => {
                const posInfo = getPositionExplanation(scenario.setup.yourPosition);
                return (
                  <HStack spacing={3} mb={3} p={2} bg="blackAlpha.300" borderRadius="md">
                    <Text fontSize="xl">{posInfo.emoji}</Text>
                    <Box>
                      <Text color="white" fontSize="sm" fontWeight="bold">
                        {posInfo.name}
                      </Text>
                      <Text color="gray.400" fontSize="xs">
                        {posInfo.explanation}
                      </Text>
                    </Box>
                  </HStack>
                );
              })()}
              
              {/* Board */}
              {scenario.setup.board.length > 0 && (
                <Box>
                  <Text color="gray.400" fontSize="xs" mb={2}>
                    {getBoardExplanation(scenario.setup.board)}
                  </Text>
                  <HStack spacing={1} flexWrap="wrap">
                    {scenario.setup.board.map((card, i) => (
                      <Badge 
                        key={i} 
                        bg="white" 
                        color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}
                        fontSize="md"
                        px={2}
                        py={1}
                      >
                        {formatCardDisplay(card)}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}
            </Box>
            
            {/*  THE ACTION */}

            <Box 
              bg={`${POKER_COLORS.gold}22`} 
              p={4} 
              borderRadius="lg"
              borderLeft="4px solid"
              borderColor={POKER_COLORS.gold}
            >
              <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize="sm" mb={2}>
                 WHAT HAPPENED

              </Text>
              <Text color="white" fontSize="sm" mb={3}>
                {scenario.setup.streetAction}
              </Text>
              {scenario.setup.villainAction && (
                <Badge colorScheme="orange" fontSize="sm" px={3} py={1} mb={3}>
                   {scenario.setup.villainAction}

                </Badge>
              )}
              <HStack spacing={6}>
                <Box>
                  <Text color="gray.500" fontSize="xs">Pot Size</Text>
                  <Text color="green.400" fontWeight="bold" fontSize="xl">${scenario.setup.potSize}</Text>
                </Box>
                {scenario.setup.amountToCall && scenario.setup.amountToCall > 0 && (
                  <Box>
                    <Text color="gray.500" fontSize="xs">To Call</Text>
                    <Text color="orange.400" fontWeight="bold" fontSize="xl">${scenario.setup.amountToCall}</Text>
                  </Box>
                )}
              </HStack>
            </Box>
            
            {/*  YOUR DECISION */}

            <Box 
              bg="whiteAlpha.100" 
              p={3} 
              borderRadius="lg"
              textAlign="center"
            >
              <Text color="white" fontWeight="bold" fontSize="md" mb={1}>
                 What should you do?

              </Text>
              <Text color="gray.400" fontSize="xs">
                This scenario tests: <Badge colorScheme="cyan" fontSize="xs">{scenario.concept.replace(/-/g, ' ').toUpperCase()}</Badge>
              </Text>
            </Box>
            
            {/* Hint Section */}
            <Box>
              <HStack
                as="button"
                w="100%"
                justify="space-between"
                p={2}
                bg="blackAlpha.300"
                borderRadius="md"
                onClick={() => setShowHint(!showHint)}
                _hover={{ bg: 'blackAlpha.400' }}
              >
                <HStack>
                  <Lightbulb size={16} color={POKER_COLORS.gold} />
                  <Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="medium">
                    Need a hint?
                  </Text>
                </HStack>
                {showHint ? <ChevronUp size={16} color="gray" /> : <ChevronDown size={16} color="gray" />}
              </HStack>
              
              <Collapse in={showHint}>
                <Box
                  mt={2}
                  p={3}
                  bg={`${POKER_COLORS.gold}11`}
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderColor={POKER_COLORS.gold}
                >
                  <Text color="gray.300" fontSize="sm">
                    {scenario.keyLesson}
                  </Text>
                  {scenario.mathBreakdown && (
                    <Box mt={2} p={2} bg="blackAlpha.400" borderRadius="md">
                      <Text color="gray.400" fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap">
                        {scenario.mathBreakdown}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
            
            {/* Poker Terms Glossary */}
            <Box>
              <HStack
                as="button"
                w="100%"
                justify="space-between"
                p={2}
                bg="blackAlpha.300"
                borderRadius="md"
                onClick={() => setShowGlossary(!showGlossary)}
                _hover={{ bg: 'blackAlpha.400' }}
              >
                <HStack>
                  <BookOpen size={16} color={POKER_COLORS.cyan} />
                  <Text color={POKER_COLORS.cyan} fontSize="sm" fontWeight="medium">
                    Poker Terms Glossary
                  </Text>
                </HStack>
                {showGlossary ? <ChevronUp size={16} color="gray" /> : <ChevronDown size={16} color="gray" />}
              </HStack>

              <Collapse in={showGlossary}>
                <Box mt={2} maxH="300px" overflowY="auto">
                  <Input
                    placeholder="Search terms (e.g. ICM, EV, pot odds...)"
                    size="sm"
                    mb={2}
                    bg="blackAlpha.400"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    value={glossarySearch}
                    onChange={(e) => setGlossarySearch(e.target.value)}
                  />
                  {(() => {
                    const search = glossarySearch.toLowerCase();
                    const entries = Object.values(POKER_GLOSSARY)
                      .filter((e: GlossaryEntry) => !search ||
                        e.term.toLowerCase().includes(search) ||
                        e.definition.toLowerCase().includes(search)
                      )
                      .sort((a: GlossaryEntry, b: GlossaryEntry) => a.term.localeCompare(b.term))
                      .slice(0, showGlossary ? 20 : 0);

                    if (entries.length === 0) return (
                      <Text color="gray.500" fontSize="xs" textAlign="center" py={2}>
                        No matching terms found
                      </Text>
                    );

                    return entries.map((entry: GlossaryEntry, idx: number) => (
                      <Box
                        key={idx}
                        p={2}
                        mb={1}
                        bg="blackAlpha.300"
                        borderRadius="md"
                        borderLeft="2px solid"
                        borderColor={
                          entry.level === 'absolute-beginner' ? 'green.400' :
                          entry.level === 'beginner' ? 'blue.400' :
                          entry.level === 'intermediate' ? 'yellow.400' : 'red.400'
                        }
                      >
                        <Text color="white" fontSize="xs" fontWeight="bold">{entry.term}</Text>
                        <Text color="gray.400" fontSize="xs">
                          {entry.simpleDefinition || entry.definition}
                        </Text>
                        {entry.example && (
                          <Text color="gray.500" fontSize="xs" mt={1} fontStyle="italic">
                            e.g. {entry.example}
                          </Text>
                        )}
                      </Box>
                    ));
                  })()}
                </Box>
              </Collapse>
            </Box>

            {/* Concept Badge */}
            <HStack flexWrap="wrap" gap={1}>
              <Badge bg={`${POKER_COLORS.cyan}22`} color={POKER_COLORS.cyan}>
                {scenario.concept.replace(/-/g, ' ')}
              </Badge>
              {scenario.conceptsToReview.slice(0, 2).map((c, i) => (
                <Badge key={i} variant="outline" colorScheme="gray" fontSize="xs">
                  {c}
                </Badge>
              ))}
            </HStack>
            
            {/* Session Stats */}
            <Box mt="auto" pt={3} borderTop="1px solid" borderColor="whiteAlpha.100">
              <Text color="gray.500" fontSize="xs" mb={2}>Session Progress</Text>
              <HStack justify="space-between" mb={1}>
                <Text color="gray.400" fontSize="sm">Accuracy</Text>
                <Text color="white" fontSize="sm" fontWeight="bold">
                  {scenariosCompleted > 0 ? Math.round((correctAnswers / scenariosCompleted) * 100) : 0}%
                </Text>
              </HStack>
              <Progress
                value={scenariosCompleted > 0 ? (correctAnswers / scenariosCompleted) * 100 : 0}
                size="sm"
                colorScheme="green"
                bg="blackAlpha.400"
                borderRadius="full"
              />
              <Text color="gray.500" fontSize="xs" mt={2}>
                {scenariosCompleted} scenarios completed
              </Text>
            </Box>
          </VStack>
        </Collapse>
        
        {/* Toggle Coach Panel Button - Desktop Only */}
        <Box
          as="button"
          onClick={() => setShowCoachPanel(!showCoachPanel)}
          h="100%"
          w="24px"
          bg="blackAlpha.400"
          borderRadius="md"
          display={{ base: 'none', md: 'flex' }}
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'blackAlpha.500' }}
        >
          {showCoachPanel ? <ChevronLeft size={16} color="gray" /> : <ChevronRight size={16} color="gray" />}
        </Box>
        
        {/* FIXED: Poker Table - Full width on mobile */}
        <VStack 
          flex={1} 
          spacing={{ base: 2, md: 3 }} 
          align="stretch"
          minH={{ base: '50vh', md: 'auto' }}
        >
          {/* Poker Table. Phones: the felt yields height to the coach panel,
              hole cards, and decision bar so everything fits on screen. */}
          <Box
            flex={1}
            position="relative"
            minH={{ base: '280px', sm: '320px', md: '400px' }}
            style={{ ['--pss-felt-h' as any]: 'max(300px, calc(100dvh - 500px))' }}
          >
            <PokerTableSVG
              players={playersForSVG}
              currentPlayerIndex={gameState.currentPlayerIndex}
              pot={gameState.pot}
              communityCards={gameState.communityCards}
              currentUserId={currentUserId}
              round={gameState.round}
            />
            
            {/* Feedback Overlay */}
            {feedback?.show && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                bg={feedback.correct ? 'green.900' : 'red.900'}
                border="3px solid"
                borderColor={feedback.correct ? 'green.400' : 'red.400'}
                borderRadius="xl"
                p={{ base: 4, md: 6 }}
                maxW={{ base: '95%', md: '500px' }}
                w="90%"
                zIndex={100}
                boxShadow="0 0 40px rgba(0,0,0,0.8)"
              >
                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                  {/* Result Header */}
                  <HStack justify="center">
                    {feedback.correct ? (
                      <CheckCircle size={28} color="#48BB78" />
                    ) : (
                      <XCircle size={28} color="#F56565" />
                    )}
                    <Heading size={{ base: 'sm', md: 'md' }} color={feedback.correct ? 'green.300' : 'red.300'}>
                      {feedback.correct ? 'Correct!' : 'Not Quite'}
                    </Heading>
                  </HStack>
                  
                  {/* Evaluation Badge */}
                  <HStack justify="center">
                    <Badge
                      fontSize={{ base: 'xs', md: 'sm' }}
                      px={3}
                      py={1}
                      borderRadius="full"
                      bg={
                        feedback.evaluation === 'excellent' ? 'green.500' :
                        feedback.evaluation === 'acceptable' ? 'yellow.500' :
                        feedback.evaluation === 'suboptimal' ? 'orange.500' : 'red.500'
                      }
                      color="white"
                    >
                      {feedback.evaluation.toUpperCase()}
                    </Badge>
                  </HStack>
                  
                  {/* Feedback Message */}
                  <Text color="gray.200" fontSize={{ base: 'xs', md: 'sm' }} textAlign="center" whiteSpace="pre-wrap">
                    {feedback.message}
                  </Text>
                  
                  {/* Key Lesson */}
                  <Box
                    p={{ base: 2, md: 3 }}
                    bg="blackAlpha.400"
                    borderRadius="md"
                    borderLeft="3px solid"
                    borderColor={POKER_COLORS.gold}
                  >
                    <HStack mb={1}>
                      <BookOpen size={14} color={POKER_COLORS.gold} />
                      <Text color={POKER_COLORS.gold} fontSize="xs" fontWeight="bold">
                        KEY LESSON
                      </Text>
                    </HStack>
                    <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="pre-wrap">
                      {feedback.lesson}
                    </Text>
                  </Box>
                  
                  {/* Next Button */}
                  <PokerButton
                    variant="primary"
                    size={{ base: 'md', md: 'lg' }}
                    onClick={handleNextScenario}
                    rightIcon={<ChevronRight size={20} />}
                  >
                    Next Scenario
                  </PokerButton>
                </VStack>
              </Box>
            )}
          </Box>
          
          {/* Player's Hole Cards */}
          {currentPlayer?.holeCards && currentPlayer.holeCards.length > 0 && !feedback?.show && (
            <VStack spacing={1}>
              <HStack spacing={2} justify="center">
                {currentPlayer.holeCards.map((card, idx) => (
                  <Box
                    key={idx}
                    bg="white"
                    borderRadius="8px"
                    w={{ base: '44px', md: '60px' }}
                    h={{ base: '62px', md: '84px' }}
                    display="flex"
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="lg"
                    border="3px solid"
                    borderColor={POKER_COLORS.gold}
                  >
                    <Text
                      fontSize={{ base: 'lg', md: 'xl' }}
                      fontWeight="bold"
                      color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}
                    >
                      {card.rank}
                    </Text>
                    <Text
                      fontSize={{ base: 'xl', md: '2xl' }}
                      color={card.suit === 'hearts' || card.suit === 'diamonds' ? 'red.500' : 'gray.800'}
                    >
                      {card.suit === 'hearts' ? '♥' : card.suit === 'diamonds' ? '♦' : card.suit === 'clubs' ? '♣' : '♠'}
                    </Text>
                  </Box>
                ))}
              </HStack>
              
              {/* Hand Strength */}
              {gameState.round !== 'preflop' && gameState.communityCards?.length > 0 && (() => {
                const hand = handEvaluationService.evaluateHand(
                  currentPlayer.holeCards,
                  gameState.communityCards
                );
                const description = hand ? handEvaluationService.getHandDescription(hand) : null;
                if (!description) return null;
                
                return (
                  <Badge
                    bg={POKER_COLORS.cyan}
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    {description}
                  </Badge>
                );
              })()}
            </VStack>
          )}
          
          {/* Raise Slider */}
          {showRaiseSlider && currentPlayer && isMyTurn && !feedback?.show && (
            <VStack
              spacing={3}
              p={{ base: 3, md: 4 }}
              bg="blackAlpha.700"
              borderRadius="lg"
              w="100%"
              maxW="400px"
              mx="auto"
            >
              <HStack w="100%" justify="space-between">
                <Text color="gray.300" fontSize="sm">Min: ${minRaise}</Text>
                <Text color="gray.300" fontSize="sm">Max: ${currentPlayer.chips.toFixed(2)}</Text>
              </HStack>
              <input
                type="range"
                min={minRaise}
                max={currentPlayer.chips}
                value={raiseAmount || minRaise}
                onChange={(e) => setRaiseAmount(Number(e.target.value))}
                style={{ width: '100%' }}
              />
              <HStack w="100%" justify="center">
                <Text color="white" fontSize="lg">$</Text>
                <Input
                  type="number"
                  value={raiseAmount || ''}
                  onChange={(e) => setRaiseAmount(Number(e.target.value) || 0)}
                  bg="blackAlpha.600"
                  color="white"
                  fontWeight="bold"
                  fontSize="xl"
                  textAlign="center"
                  w="120px"
                  border="2px solid"
                  borderColor="yellow.500"
                />
              </HStack>
              <HStack spacing={2} flexWrap="wrap" justify="center">
                {/* Sizing counts the call and clamps to the legal range -
                    raw pot/2 under-sizes below the minimum when facing a bet. */}
                <PokerButton size="sm" onClick={() => {
                  const callAmt = Math.max(0, (gameState.currentBet || 0) - (currentPlayer.currentBet || 0));
                  setRaiseAmount(Math.min(Math.max(Math.round((gameState.pot + callAmt) / 2), minRaise), currentPlayer.chips));
                }}>
                  ½ Pot
                </PokerButton>
                <PokerButton size="sm" onClick={() => {
                  const callAmt = Math.max(0, (gameState.currentBet || 0) - (currentPlayer.currentBet || 0));
                  setRaiseAmount(Math.min(Math.max(Math.round(gameState.pot + callAmt), minRaise), currentPlayer.chips));
                }}>
                  Pot
                </PokerButton>
                <PokerButton size="sm" onClick={() => setRaiseAmount(currentPlayer.chips)} bg="orange.500">
                  ALL-IN
                </PokerButton>
              </HStack>
              <HStack spacing={3}>
                <PokerButton
                  onClick={() => handleAction('raise', raiseAmount)}
                  isDisabled={isProcessingAction || raiseAmount < minRaise}
                  bg="green.500"
                >
                  {gameState.currentBet === 0 ? `BET $${raiseAmount}` : `RAISE TO $${(maxBet + raiseAmount).toFixed(2)}`}
                </PokerButton>
                <PokerButton variant="secondary" onClick={() => setShowRaiseSlider(false)}>
                  Cancel
                </PokerButton>
              </HStack>
            </VStack>
          )}
          
          {/* Action Buttons -  IMPROVED: Beginner-friendly with explanations */}

          {isMyTurn && currentPlayer && !feedback?.show && (
            <VStack
              spacing={2}
              p={{ base: 3, md: 4 }}
              bg={`linear-gradient(180deg, ${POKER_COLORS.darkTeal}ee 0%, ${POKER_COLORS.darkBg || '#0A1F2E'}ee 100%)`}
              borderRadius="lg"
              border="2px solid"
              borderColor="whiteAlpha.200"
              w="100%"
              maxW="500px"
              mx="auto"
            >
              {/* Quick Decision Label - desktop only; phones get the same
                  compact single-row bar as every other table */}
              <Text color="gray.400" fontSize="xs" textAlign="center"
                display={{ base: 'none', md: 'block' }}>
                 Tap your decision:

              </Text>
              
              <HStack 
                spacing={{ base: 2, md: 3 }} 
                justify="center" 
                flexWrap={{ base: 'nowrap', md: 'wrap' }}
                gap={2}
                w="100%"
              >
                {/* Fold - with explanation */}
                <VStack spacing={0} flex={{ base: 1, md: 'initial' }}>
                  <PokerButton
                    onClick={() => handleAction('fold')}
                    isDisabled={isProcessingAction}
                    bg="red.600"
                    _hover={{ bg: 'red.500' }}
                    size={{ base: 'lg', md: 'lg' }}
                    flex={{ base: 1, md: 'initial' }}
                    minW={{ base: 0, md: '110px' }}
                    h={{ base: '50px', md: '55px' }}
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="bold"
                  >
                     FOLD

                  </PokerButton>
                  <Text color="gray.500" fontSize="2xs" mt={1} display={{ base: 'none', md: 'block' }}>Give up</Text>
                </VStack>
                
                {/* Check / Call - with explanation */}
                {canCheck ? (
                  <VStack spacing={0} flex={{ base: 1, md: 'initial' }}>
                    <PokerButton
                      onClick={() => handleAction('check')}
                      isDisabled={isProcessingAction}
                      bg="green.500"
                      _hover={{ bg: 'green.400' }}
                      size={{ base: 'lg', md: 'lg' }}
                      flex={{ base: 1, md: 'initial' }}
                      minW={{ base: 0, md: '120px' }}
                      h={{ base: '50px', md: '55px' }}
                      fontSize={{ base: 'md', md: 'lg' }}
                      fontWeight="bold"
                    >
                       CHECK

                    </PokerButton>
                    <Text color="gray.500" fontSize="2xs" mt={1} display={{ base: 'none', md: 'block' }}>Free card</Text>
                  </VStack>
                ) : (
                  <VStack spacing={0} flex={{ base: 1, md: 'initial' }}>
                    <PokerButton
                      onClick={() => handleAction('call')}
                      isDisabled={isProcessingAction}
                      bg="green.500"
                      _hover={{ bg: 'green.400' }}
                      size={{ base: 'lg', md: 'lg' }}
                      flex={{ base: 1, md: 'initial' }}
                      minW={{ base: 0, md: '130px' }}
                      h={{ base: '50px', md: '55px' }}
                      fontSize={{ base: 'md', md: 'lg' }}
                      fontWeight="bold"
                    >
                       CALL ${callAmount.toFixed(0)}

                    </PokerButton>
                    <Text color="gray.500" fontSize="2xs" mt={1} display={{ base: 'none', md: 'block' }}>Match bet</Text>
                  </VStack>
                )}
                
                {/* Raise / Bet - with explanation */}
                {canRaise && (
                  <VStack spacing={0} flex={{ base: 1, md: 'initial' }}>
                    <PokerButton
                      onClick={() => {
                        setRaiseAmount(minRaise);
                        setShowRaiseSlider(!showRaiseSlider);
                      }}
                      isDisabled={isProcessingAction}
                      bg={POKER_COLORS.gold}
                      color="black"
                      _hover={{ bg: 'yellow.400' }}
                      size={{ base: 'lg', md: 'lg' }}
                      flex={{ base: 1, md: 'initial' }}
                      minW={{ base: 0, md: '120px' }}
                      h={{ base: '50px', md: '55px' }}
                      fontSize={{ base: 'md', md: 'lg' }}
                      fontWeight="bold"
                    >
                       {gameState.currentBet === 0 ? 'BET' : 'RAISE'}

                    </PokerButton>
                    <Text color="gray.500" fontSize="2xs" mt={1} display={{ base: 'none', md: 'block' }}>
                      {gameState.currentBet === 0 ? 'Make a bet' : 'Bet more'}
                    </Text>
                  </VStack>
                )}
                
                {/* All-In - only show if significant */}
                {currentPlayer.chips > 0 && currentPlayer.chips > callAmount * 3 && (
                  <VStack spacing={0} flex={{ base: 1, md: 'initial' }}>
                    <PokerButton
                      onClick={() => handleAction('all-in')}
                      isDisabled={isProcessingAction}
                      bg="orange.500"
                      _hover={{ bg: 'orange.400' }}
                      size={{ base: 'lg', md: 'lg' }}
                      flex={{ base: 1, md: 'initial' }}
                      minW={{ base: 0, md: '100px' }}
                      h={{ base: '50px', md: '55px' }}
                      fontSize={{ base: 'md', md: 'lg' }}
                      fontWeight="bold"
                    >
                       ALL-IN

                    </PokerButton>
                    <Text color="gray.500" fontSize="2xs" mt={1} display={{ base: 'none', md: 'block' }}>Risk it all</Text>
                  </VStack>
                )}
              </HStack>
            </VStack>
          )}
        </VStack>
      </Flex>
    </Box>
  );
};

// ============================================================================
// MISSING ICON COMPONENT
// ============================================================================

const ChevronLeft: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="15,18 9,12 15,6" />
  </svg>
);

// ============================================================================
// EXPORT
// ============================================================================

export default ScenarioTablePage;